import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";
import {
  FourColRow,
  StepCard,
  StepCardIcon,
  StepCardTitle,
  StepCardDesc,
  SectionBottomImage,
} from "../../components/ContentLayouts";

// ── Image imports ─────────────────────────────────────────────────────────────
import imgConsequences   from "../../assets/images/content/pet-vaccination/consequences.jpg";
import iconFindNearYou1  from "../../assets/images/icons/location.png";
import iconFindNearYou2  from "../../assets/images/icons/review-list.png";
import iconFindNearYou3  from "../../assets/images/icons/select-app.png";
import iconFindNearYou4  from "../../assets/images/icons/med-calendar.png";
import imgNearYou        from "../../assets/images/content/pet-vaccination/near-you.jpg";

const faqs = [
  {
    question: "Why are vaccinations important for pets?",
    answer:
      "Vaccinations prepare your pet's immune system to fight off disease-causing organisms. They prevent serious, costly, and sometimes fatal diseases from affecting your furry friend.",
  },
  {
    question: "What vaccines does my dog need?",
    answer:
      "Dogs generally need core vaccines, which include Rabies, Canine Distemper, Parvovirus, and Adenovirus. Depending on your dog's lifestyle and environment, a vet may also recommend non-core vaccines like Bordetella (Kennel Cough) or Leptospirosis.",
  },
  {
    question: "What vaccines does my cat need?",
    answer:
      "Cats require core vaccines such as Rabies, Feline Viral Rhinotracheitis, Calicivirus, and Panleukopenia (FVRCP). Outdoor cats or those in multi-cat households might also need the Feline Leukemia Virus (FeLV) vaccine.",
  },
  {
    question: "How often should pets receive vaccinations?",
    answer:
      "Puppies and kittens require a series of vaccinations starting at about 6 to 8 weeks of age, with boosters given every 3 to 4 weeks until they are around 16 weeks old. Adult pets typically need booster shots either annually or every three years, depending on the specific vaccine and your vet's advice.",
  },
  {
    question: "Are pet vaccinations safe?",
    answer:
      "Yes, vaccines are rigorously tested for safety and efficacy. While some pets may experience mild side effects like a slight fever, lethargy, or soreness at the injection site, severe allergic reactions are very rare. The protective benefits far outweigh the minor risks.",
  },
  {
    question: "Can I find a pet vaccination clinic near me using PetQura?",
    answer:
      "Absolutely. PetQura is specifically built to help pet parents in Bangalore locate the closest veterinary clinics based on the exact services they need.",
  },
  {
    question: "Does the pet vaccination cost vary by location in Bangalore?",
    answer:
      "Yes, costs can vary slightly depending on the specific pet vaccination clinic, the brand of the vaccine used, and whether you are opting for an in-clinic visit or at home pet vaccinations.",
  },
  {
    question: "How do I prepare my pet for their appointment?",
    answer:
      "Keep your pet calm and relaxed before the pet vaccination visit. Bring any previous medical records or pet vaccination card booklets you have. For cats and small dogs, use a secure, comfortable carrier. For larger dogs, ensure they are on a sturdy leash.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetVaccination = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Pet Vaccination Clinic Near You in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Book at home pet vaccinations in Bangalore or find a pet vaccination clinic near you. Check pet vaccination cost, compare clinics and schedule your pet's shots in minutes."
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>Pet Vaccinations With PetQura in Bangalore</h1>
        </HeroSection>

        <EmbeddedClinicList serviceId={null} previewMode />

        <ContentSection>
          <p>
            Welcome to PetQura! As pet parents, we absolutely love our furry
            friends, and keeping them safe is our top priority. We know you want
            the absolute best for your companions, and one of the most important
            steps in keeping them happy and healthy is ensuring they receive
            timely pet vaccinations.
          </p>
          <p>
            Find pet vaccinations near you, as stress-free as possible for both
            you and your pet. If you are in Bangalore, our platform helps you to
            easily find the closest veterinary clinics based on the specific pet
            vaccination services you need. Let's walk through why staying on top
            of your pet's immunization schedule is essential for their
            well-being.
          </p>
        </ContentSection>

        {/* ── Consequences — Pattern F (full-width text + bottom image) ── */}
        <ContentSection>
          <SectionHeading>
            The Consequences of Not Vaccinating Your Pet
          </SectionHeading>
          <p>
            Skipping a visit to a pet vaccination clinic can lead to serious
            health issues for your beloved companion. Unvaccinated pets are at a
            much higher risk of contracting severe, highly contagious, and
            sometimes fatal diseases.
          </p>
          <p>
            When pets miss their shots, the consequences can affect both them
            and the broader pet community:
          </p>
          <BulletList>
            <li>
              <strong>Risk of severe disease:</strong> Unvaccinated dogs are
              highly vulnerable to Parvovirus and Distemper, which are severe
              and difficult to treat. Cats face high risks from Feline Leukemia
              and Panleukopenia.
            </li>
            <li>
              <strong>Spread of infection:</strong> Pets that are not immunized
              can easily catch and spread diseases to other animals at the park,
              boarding facilities, or even in your neighborhood.
            </li>
            <li>
              <strong>Zoonotic diseases:</strong> Certain illnesses, like
              Rabies, can be transmitted from pets to humans, making it very
              important to get the pet rabies vaccine.
            </li>
            <li>
              <strong>Higher medical bills:</strong> Treating a preventable
              disease often requires hospitalization, emergency care, and
              expensive medications, which costs significantly more than routine
              immunizations.
            </li>
          </BulletList>
          <p>
            Don't wait until it is too late. You can use PetQura to book
            reliable pet vaccination services today and keep your pet shielded
            from these preventable dangers.
          </p>
          <SectionBottomImage
            src={imgConsequences}
            alt="The consequences of not vaccinating your pet"
          />
        </ContentSection>

        <ContentSection>
          <SectionHeading>
            Pet Vaccination Cost &amp; What's Included
          </SectionHeading>
          <p>
            When planning your vet visit to the pet vaccination centre, it is
            natural to wonder about the pet vaccination cost and what exactly is
            included in the service. We believe in keeping things transparent,
            factual, and straightforward so you know exactly what to expect.
          </p>
          <p>
            The overall pet vaccination cost in Bangalore typically depends on a
            few straightforward factors:
          </p>
          <BulletList>
            <li>
              <strong>Type of vaccine:</strong> Core vaccines (like Rabies and
              DHLPP for dogs, or FVRCP for cats) are priced differently than
              non-core lifestyle vaccines (like Kennel Cough).
            </li>
            <li>
              <strong>Consultation fees:</strong> Most clinics include a general
              health check-up before administering the shot to ensure your pet
              is healthy enough to receive it.
            </li>
            <li>
              <strong>Clinic and home visit:</strong> Booking an in-clinic
              appointment usually costs less, while opting for at home pet
              vaccinations are at a slightly different price point.
            </li>
            <li>
              <strong>Pet's age and weight:</strong> Puppies and kittens often
              require a series of booster shots over a few weeks, while adult
              pets typically need annual boosters.
            </li>
          </BulletList>
          <p>
            Ultimately, the pet vaccination cost is a small price to pay
            compared to the emotional and financial toll of treating a severe
            illness.
          </p>
        </ContentSection>

        {/* ── Find Near You — Pattern G (intro / 4-col steps / closing / image) ── */}
        <ContentSection>
          <SectionHeading>Find Pet Vaccinations Near You</SectionHeading>
          <p>
            We know that traveling long distances in Bangalore traffic with a
            nervous pet can be challenging. That is exactly why PetQura is
            designed to help you quickly find pet vaccinations near you. As good
            as searching "pet vaccinations near me". We want your pet's
            healthcare journey to be calm and predictable.
          </p>
          <p>
            Finding a reliable pet vaccination centre takes just a few clicks:
          </p>
          <FourColRow>
            <StepCard>
              <StepCardIcon src={iconFindNearYou1} alt="Enter your location" />
              <StepCardTitle>Enter Your Location</StepCardTitle>
              <StepCardDesc>
                Simply tell our platform where you are located within Bangalore.
              </StepCardDesc>
            </StepCard>
            <StepCard>
              <StepCardIcon src={iconFindNearYou2} alt="Select your service" />
              <StepCardTitle>Select Your Service</StepCardTitle>
              <StepCardDesc>
                Choose the pet vaccination services or health check-ups your
                pet needs right now.
              </StepCardDesc>
            </StepCard>
            <StepCard>
              <StepCardIcon src={iconFindNearYou3} alt="Pick a clinic" />
              <StepCardTitle>Pick a Clinic</StepCardTitle>
              <StepCardDesc>
                Browse a list of the closest veterinary professionals and find
                a pet vaccination clinic that fits your schedule.
              </StepCardDesc>
            </StepCard>
            <StepCard>
              <StepCardIcon src={iconFindNearYou4} alt="Book the appointment" />
              <StepCardTitle>Book the Appointment</StepCardTitle>
              <StepCardDesc>
                Confirm your time slot online and reduce time spent waiting.
                Don't forget to carry your pet vaccination card.
              </StepCardDesc>
            </StepCard>
          </FourColRow>
          <p>
            Finding pet vaccinations near you has never been this easy. By
            connecting you with local clinics, we help reduce travel anxiety for
            your pet and save you valuable time.
          </p>
          <SectionBottomImage
            src={imgNearYou}
            alt="Find pet vaccinations near you in Bangalore"
          />
        </ContentSection>

        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetVaccination;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
